import { DialogComponent } from "@theme/dialog";
import { ThemeEvents, VariantUpdateEvent } from "@theme/events";

/**
 * The drawer opened by the Inquire button in Buy buttons.
 *
 * Shopify's contact form reloads the page when it is sent, which closes the drawer, so the drawer
 * reopens itself when the page comes back at its form's #anchor, showing the form's confirmation
 * or error. Closing it then strips ?contact_posted and the anchor from the URL and brings the
 * form back, so a refresh doesn't reopen it. In the theme editor it also opens when one of its
 * form blocks is selected.
 *
 * The form's hidden product fields, and the product summary beside the form, describe the variant
 * selected when the page loaded; they are swapped for fresh ones each time the shopper picks
 * another variant.
 *
 * @extends DialogComponent
 */
class InquireDrawer extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();

    if (this.#returnedToForm) this.showDialog();
    this.refs.dialog.addEventListener("close", this.#handleClose);

    this.closest(".shopify-section")?.addEventListener(
      ThemeEvents.variantUpdate,
      this.#handleVariantUpdate,
    );

    if (window.Shopify?.designMode) {
      document.addEventListener(
        "shopify:block:select",
        this.#handleBlockSelect,
      );
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener("close", this.#handleClose);
    this.closest(".shopify-section")?.removeEventListener(
      ThemeEvents.variantUpdate,
      this.#handleVariantUpdate,
    );
    document.removeEventListener(
      "shopify:block:select",
      this.#handleBlockSelect,
    );
  }

  /**
   * Whether Shopify sent the shopper back here after a send. The form's action is
   * /contact#<form id>, so the page comes back at the form's own anchor.
   */
  get #returnedToForm() {
    const form = this.refs.dialog.querySelector("form");
    return Boolean(form?.id) && window.location.hash === `#${form?.id}`;
  }

  /**
   * After a send, closing the drawer (Close, the overlay or Escape) clears the sent state.
   */
  #handleClose = () => {
    const url = new URL(window.location.href);
    const returnedToForm = this.#returnedToForm;
    if (!returnedToForm && !url.searchParams.has("contact_posted")) return;

    url.searchParams.delete("contact_posted");
    if (returnedToForm) url.hash = "";
    history.replaceState(history.state, "", url);

    // :target doesn't notice replaceState, so the form's messages are removed by hand
    this.refs.dialog
      .querySelectorAll(".custom-form__success, .custom-form__error")
      .forEach((message) => message.remove());
  };

  /**
   * Takes the product fields from this drawer's copy in the section the variant picker fetched.
   * @param {VariantUpdateEvent} event
   */
  #handleVariantUpdate = (event) => {
    const { html, productId, newProduct } = event.detail.data;
    if (!newProduct && productId && productId !== this.dataset.productId)
      return;

    const fresh = html.getElementById(this.id);
    if (!fresh) return;
    if (newProduct) this.dataset.productId = newProduct.id;

    // The product summary beside the form: the variant's image and price
    const freshProduct = fresh.querySelector("[data-inquire-product]");
    const oldProduct = this.refs.dialog.querySelector("[data-inquire-product]");
    if (freshProduct && oldProduct) oldProduct.replaceWith(freshProduct);

    const freshFields = fresh.querySelectorAll("form [data-product-field]");
    const oldFields = this.refs.dialog.querySelectorAll(
      "form [data-product-field]",
    );
    if (!freshFields.length || !oldFields.length) return;

    oldFields[0]?.before(...freshFields);
    oldFields.forEach((field) => field.remove());
  };

  /**
   * @param {Event} event
   */
  #handleBlockSelect = (event) => {
    if (
      event.target instanceof Node &&
      this.refs.dialog.contains(event.target)
    ) {
      this.showDialog();
    }
  };
}

if (!customElements.get("inquire-drawer"))
  customElements.define("inquire-drawer", InquireDrawer);
